/**
 * Operational State — single shared model for Ops Dashboard + Control Plane.
 *
 * Both UIs must assemble via assembleOperationalState() from the same inputs
 * (platform/operations + customer + integrations/dns/ssl/knowledge). Neither
 * may re-derive conflicting health / doctor / incident / readiness.
 */

import { mapDoctorLabel } from "./doctor";
import {
  dnsDetailWithEvidence,
  formatDnsEvidence,
  formatSslEvidence,
  sslDetailWithEvidence,
} from "./evidence";
import { resolveTenantHealthLabel } from "./health";
import {
  PRODUCTION_READY_REQUIREMENTS,
  checklistLabel,
  deriveOnboarding,
  deriveOnboardingReadiness,
} from "./onboarding";
import { derivePlatformHealthVerdict } from "./platformHealth";
import {
  deriveRuntimeHealthDomains,
  type RuntimeHealthComponent,
  type RuntimeHealthDomain,
} from "./runtimeDomains";
import {
  buildVerificationTimeline,
  normalizeVerificationResults,
  type VerificationResult,
} from "./verification";
import { LABEL } from "./terminology";
import type {
  ChecklistState,
  CustomerStatusInput,
  DnsStatusInput,
  DoctorLabel,
  HealthLabel,
  IntegrationStatusInput,
  KnowledgeStatusInput,
  OnboardingItem,
  OnboardingReadiness,
  PlatformHealthVerdict,
  PlatformVersionInput,
  RuntimeHealthRow,
  SslStatusInput,
  TenantHealthInput,
  VersionFields,
} from "./types";
import { deriveVersionFields } from "./version";

/** UX framing — same Operational State, different job. */
export const OPERATIONAL_FRAMING = {
  ops: "What requires attention?",
  controlPlane: "Why is it happening?",
} as const;

export type EvidenceKind =
  | "doctor_check"
  | "dns"
  | "ssl"
  | "knowledge"
  | "health"
  | "deployment"
  | "attention"
  | "log_placeholder";

export type EvidenceRef = {
  kind: EvidenceKind;
  label: string;
  value: string;
  /** Real evidence URL only — never invent. */
  url: string | null;
  observedAt: string | null;
};

export type ConfidenceState = {
  /** Honest: only a real score, otherwise "Confidence unavailable". */
  label: "Confidence unavailable" | string;
  score: number | null;
  reasons: string[];
};

export type IncidentStateSlice = {
  id: string;
  severity: string;
  problem: string;
  recommendedAction: string;
  checkId: string;
  occurredAt: string | null;
  evidence: EvidenceRef[];
};

export type RuntimeDoctorSlice = {
  label: DoctorLabel;
  raw: string | null;
  /** Current Result — same label language as RuntimeDoctorStatus. */
  currentResult: DoctorLabel;
  rootCause: string | null;
  recommendedAction: string | null;
  /** Deep-link hint — UIs open existing doctor report, never a duplicate page. */
  reportHref: string;
  reportLabel: "View Runtime Doctor Report";
  lastScanAt: string | null;
};

export type ProductionReadyCheckItem = {
  id: string;
  label: string;
  state: ChecklistState;
  stateLabel: string;
  /** Observed / current value from evidence. */
  current: string;
  /** Expected production-ready condition. */
  expected: string;
  evidence: string | null;
  recommendedAction: string | null;
  /** True when this item blocks Production Ready. */
  blocking: boolean;
};

export type ProductionReadinessSlice = {
  passed: number;
  total: number;
  summary: string;
  readiness: OnboardingReadiness;
  items: ProductionReadyCheckItem[];
  remaining: ProductionReadyCheckItem[];
};

export type RuntimeHealthSliceRow = RuntimeHealthRow & {
  /** Why Not monitored / Unknown — never invent Healthy. */
  explanation: string | null;
  domain?: RuntimeHealthDomain["id"];
  reason?: string | null;
  evidence?: string | null;
  recommendation?: string | null;
  lastVerification?: string | null;
  legacyId?: string | null;
};

export type DnsEvidenceSlice = {
  state: string | null;
  ok: boolean | null;
  hostname: string | null;
  expectedIp: string | null;
  resolvedIps: string[];
  matchedExpected: boolean | null;
  resolutionMode: string | null;
  proxiedViaCloudflare: boolean | null;
  httpsReachable: boolean | null;
  tlsValid: boolean | null;
  applicationResponds: boolean | null;
  verifications: VerificationResult[];
  evidence: string | null;
  detail: string;
};

export type SslEvidenceSlice = {
  state: string | null;
  ok: boolean | null;
  hostname: string | null;
  issuer: string | null;
  expiresAt: string | null;
  daysRemaining: number | null;
  hostnameMatches: boolean | null;
  verifications: VerificationResult[];
  evidence: string | null;
  detail: string;
};

export type KnowledgeEvidenceSlice = {
  ready: boolean | null;
  pdfCount: number | null;
  chunksIndexed: number | null;
  /** Search/index verify detail from API — never invent. */
  detail: string;
  healthProbed: boolean | null;
  manualsDir: string | null;
  source: string | null;
  lastVerifyOk: boolean | null;
  lastVerifyMessage: string | null;
  lastVerifiedAt: string | null;
};

export type LogAttachmentStub = {
  status: "structured_for_later";
  message: string;
  /** Slot for future log center attachment — do not implement Log Center here. */
  entries: EvidenceRef[];
};

export type AttentionInput = {
  id: string;
  severity: string;
  tenant_slug?: string;
  tenantSlug?: string;
  check_id?: string;
  checkId?: string;
  problem: string;
  recommended_action?: string;
  recommendedAction?: string;
  occurred_at?: string | null;
  occurredAt?: string | null;
};

export type OperationalStateInput = {
  slug: string;
  displayName?: string | null;
  customer: CustomerStatusInput;
  tenantHealth: TenantHealthInput | null;
  integrations?: IntegrationStatusInput[] | null;
  knowledge?: KnowledgeStatusInput | null;
  dns?: DnsStatusInput | null;
  ssl?: SslStatusInput | null;
  platformVersion?: PlatformVersionInput | null;
  /** Attention/incident rows for this tenant from platform/operations. */
  attention?: AttentionInput[] | null;
  /** Optional real doctor message (check text) when present. */
  doctorMessage?: string | null;
  /** Optional last doctor / ops verification timestamp. */
  lastVerification?: string | null;
  /** Platform-domain VerificationResult records (CP backend, auth, DB). */
  platformVerifications?: import("./verification").VerificationResult[] | null;
  /** Real AI confidence only — omit or null → Confidence unavailable. */
  confidenceScore?: number | null;
  confidenceReasons?: string[] | null;
};

export type OperationalState = {
  slug: string;
  displayName: string | null;
  framing: typeof OPERATIONAL_FRAMING;
  platformHealth: PlatformHealthVerdict & {
    recommendedAction: string | null;
  };
  tenantHealth: HealthLabel;
  incident: IncidentStateSlice | null;
  incidents: IncidentStateSlice[];
  runtimeDoctor: RuntimeDoctorSlice;
  productionReadiness: ProductionReadinessSlice;
  deployment: VersionFields;
  /** Domain-grouped Runtime Health (Platform / Tenant Runtime / Integrations / Infrastructure). */
  runtimeHealthDomains: RuntimeHealthDomain[];
  /** Flattened components — same membership as domains (compat). */
  runtimeHealth: RuntimeHealthSliceRow[];
  dns: DnsEvidenceSlice;
  ssl: SslEvidenceSlice;
  knowledge: KnowledgeEvidenceSlice;
  rootCause: string | null;
  recommendedAction: string | null;
  evidence: EvidenceRef[];
  confidence: ConfidenceState;
  lastVerification: string | null;
  logAttachment: LogAttachmentStub;
  /** Unified verification audit trail from probes (DNS, SSL, doctor, etc.). */
  verificationTimeline: import("./verification").VerificationTimelineEntry[];
  /** Full onboarding checklist (same deriveOnboarding as before). */
  onboarding: OnboardingItem[];
};

const EXPECTED_BY_ID: Record<string, string> = {
  "runtime-doctor": "Runtime Doctor Pass",
  "ai-provider": "AI Connected (verified live test)",
  email: "Email Connected (verified live test)",
  shopify: "Shopify Connected (verified live test)",
  dns: "DNS verified (operational reachability)",
  ssl: "SSL verified (valid certificate)",
  "knowledge-base": "Knowledge Base ready (indexed + searchable)",
  "tenant-created": "Tenant registry entry exists",
  secrets: "Tenant stack .env present on host",
  database: "Provision completed timestamp recorded",
  "production-ready": "All production requirements verified",
};

function attentionSlug(item: AttentionInput): string {
  return (item.tenant_slug ?? item.tenantSlug ?? "").trim();
}

function attentionCheckId(item: AttentionInput): string {
  return (item.check_id ?? item.checkId ?? "").trim() || "unknown";
}

function attentionAction(item: AttentionInput): string {
  return (
    (item.recommended_action ?? item.recommendedAction ?? "").trim() || "Investigate"
  );
}

function attentionOccurred(item: AttentionInput): string | null {
  const raw = item.occurred_at ?? item.occurredAt ?? null;
  return raw ? String(raw) : null;
}

function mapIncident(item: AttentionInput): IncidentStateSlice {
  const checkId = attentionCheckId(item);
  const problem = (item.problem ?? "").trim() || "Check failed";
  const evidence: EvidenceRef[] = [
    {
      kind: "attention",
      label: `Check ${checkId}`,
      value: problem,
      url: null,
      observedAt: attentionOccurred(item),
    },
  ];
  return {
    id: item.id,
    severity: String(item.severity || "medium"),
    problem,
    recommendedAction: attentionAction(item),
    checkId,
    occurredAt: attentionOccurred(item),
    evidence,
  };
}

function recommendedActionForItem(item: OnboardingItem): string | null {
  if (item.state === "complete") return null;
  switch (item.id) {
    case "runtime-doctor":
      return "Run Runtime Doctor and resolve failing checks";
    case "ai-provider":
      return "Configure AI and run a successful connection test";
    case "email":
      return "Configure Email and run a successful connection test";
    case "shopify":
      return "Configure Shopify and run a successful connection test";
    case "dns":
      return "Run Test DNS — use Cloudflare Proxy or point hostname to the origin IP";
    case "ssl":
      return "Run Test SSL and renew/reissue TLS if needed";
    case "knowledge-base":
      return "Upload manuals, reindex, and verify Knowledge Base readiness";
    case "secrets":
      return "Provision or redeploy tenant stack so .env exists on host";
    case "database":
      return "Complete tenant provisioning";
    case "production-ready":
      return "Resolve remaining production requirements";
    default:
      return item.detail || "Review and complete this requirement";
  }
}

function buildProductionReadiness(
  onboarding: OnboardingItem[],
  readiness: OnboardingReadiness,
): ProductionReadinessSlice {
  const checks = onboarding.filter((i) => i.id !== "production-ready");
  const requirementIds = new Set(
    PRODUCTION_READY_REQUIREMENTS.map((r) => r.id as string),
  );
  const items: ProductionReadyCheckItem[] = checks.map((item) => {
    const blocking =
      requirementIds.has(item.id) ||
      item.id === "secrets" ||
      item.id === "database";
    return {
      id: item.id,
      label: item.label,
      state: item.state,
      stateLabel: checklistLabel(item.state),
      current: item.detail,
      expected: EXPECTED_BY_ID[item.id] ?? "Requirement verified",
      evidence: item.state === "complete" ? item.detail : item.detail || null,
      recommendedAction: recommendedActionForItem(item),
      blocking,
    };
  });
  const passed = items.filter((i) => i.state === "complete").length;
  const total = items.length;
  const remaining = items.filter((i) => i.state !== "complete");
  return {
    passed,
    total,
    summary: `${passed} / ${total} checks passed`,
    readiness,
    items,
    remaining,
  };
}

function buildDns(dns?: DnsStatusInput | null): DnsEvidenceSlice {
  if (!dns) {
    return {
      state: null,
      ok: null,
      hostname: null,
      expectedIp: null,
      resolvedIps: [],
      matchedExpected: null,
      resolutionMode: null,
      proxiedViaCloudflare: null,
      httpsReachable: null,
      tlsValid: null,
      applicationResponds: null,
      verifications: [],
      evidence: null,
      detail: "DNS status not loaded",
    };
  }
  const verifications = normalizeVerificationResults(dns.verifications);
  return {
    state: dns.state ?? null,
    ok: dns.ok ?? null,
    hostname: dns.hostname ?? null,
    expectedIp: dns.expected_ip ?? null,
    resolvedIps: dns.resolved_ips ?? [],
    matchedExpected: dns.matched_expected ?? null,
    resolutionMode: dns.resolution_mode ?? null,
    proxiedViaCloudflare: dns.proxied_via_cloudflare ?? null,
    httpsReachable: dns.https_reachable ?? null,
    tlsValid: dns.tls_valid ?? null,
    applicationResponds: dns.application_responds ?? null,
    verifications,
    evidence: formatDnsEvidence(dns),
    detail: dnsDetailWithEvidence(dns),
  };
}

function buildSsl(ssl?: SslStatusInput | null): SslEvidenceSlice {
  if (!ssl) {
    return {
      state: null,
      ok: null,
      hostname: null,
      issuer: null,
      expiresAt: null,
      daysRemaining: null,
      hostnameMatches: null,
      verifications: [],
      evidence: null,
      detail: "SSL status not loaded",
    };
  }
  const issuer = (ssl.issuer ?? "").trim() || null;
  const verifications = normalizeVerificationResults(ssl.verifications);
  return {
    state: ssl.state ?? null,
    ok: ssl.ok ?? null,
    hostname: ssl.hostname ?? null,
    issuer,
    expiresAt: ssl.expires_at ?? null,
    daysRemaining: ssl.days_remaining ?? null,
    hostnameMatches: ssl.hostname_matches ?? null,
    verifications,
    evidence: formatSslEvidence(ssl),
    detail: sslDetailWithEvidence(ssl),
  };
}

function buildKnowledge(
  knowledge?: KnowledgeStatusInput | null,
): KnowledgeEvidenceSlice {
  if (!knowledge) {
    return {
      ready: null,
      pdfCount: null,
      chunksIndexed: null,
      detail: "Knowledge status not loaded",
      healthProbed: null,
      manualsDir: null,
      source: null,
      lastVerifyOk: null,
      lastVerifyMessage: null,
      lastVerifiedAt: null,
    };
  }
  return {
    ready: knowledge.ready,
    pdfCount: knowledge.pdf_count,
    chunksIndexed: knowledge.chunks_indexed,
    detail: knowledge.detail,
    healthProbed: knowledge.health_probed ?? null,
    manualsDir: knowledge.manuals_dir ?? null,
    source: knowledge.source ?? null,
    lastVerifyOk: knowledge.last_verify_ok ?? null,
    lastVerifyMessage: knowledge.last_verify_message ?? null,
    lastVerifiedAt: knowledge.last_verified_at ?? null,
  };
}

function flattenDomainComponents(
  domains: RuntimeHealthDomain[],
): RuntimeHealthSliceRow[] {
  return domains.flatMap((d) =>
    d.components.map((c: RuntimeHealthComponent) => ({
      id: c.id,
      name: c.name,
      label: c.label,
      explanation: c.explanation,
      domain: c.domain,
      reason: c.reason,
      evidence: c.evidence,
      recommendation: c.recommendation,
      lastVerification: c.lastVerification,
      legacyId: c.legacyId,
    })),
  );
}

function buildConfidence(
  score: number | null | undefined,
  reasons: string[] | null | undefined,
): ConfidenceState {
  if (score == null || Number.isNaN(score)) {
    return {
      label: "Confidence unavailable",
      score: null,
      reasons: [],
    };
  }
  return {
    label: `${Math.round(score * 100)}%`,
    score,
    reasons: reasons?.filter(Boolean) ?? [],
  };
}

/**
 * Assemble ONE Operational State from existing API evidence.
 * Prefer this over ad-hoc health/doctor/readiness derivation in app code.
 */
export function assembleOperationalState(
  input: OperationalStateInput,
): OperationalState {
  const {
    slug,
    customer,
    tenantHealth,
    integrations,
    knowledge,
    dns,
    ssl,
    platformVersion,
  } = input;

  const scopedAttention = (input.attention ?? []).filter(
    (a) => attentionSlug(a) === slug,
  );
  const incidents = scopedAttention.map(mapIncident);
  const primary = incidents[0] ?? null;

  const onboarding = deriveOnboarding(
    customer,
    tenantHealth,
    integrations,
    knowledge,
    dns,
    ssl,
  );
  const readiness = deriveOnboardingReadiness(
    customer,
    tenantHealth,
    integrations,
    knowledge,
    dns,
    ssl,
  );
  const productionReadiness = buildProductionReadiness(onboarding, readiness);

  const doctorRaw = tenantHealth?.doctor_status ?? null;
  const doctorLabel = tenantHealth
    ? mapDoctorLabel(tenantHealth.doctor_status)
    : (LABEL.unknown as DoctorLabel);

  const tenantHealthLabel = resolveTenantHealthLabel({
    opsHealth: tenantHealth?.health,
    registryHealth: customer.last_health_status,
    hasStack: tenantHealth?.has_stack,
    hasRegistryObservation: Boolean(customer.last_health_status),
  });

  const attentionProblem = primary?.problem ?? null;
  const doctorMessage = (input.doctorMessage ?? "").trim() || null;
  const recommendedFromIncident = primary?.recommendedAction ?? null;

  const platformRecommendedPreview =
    recommendedFromIncident ||
    (tenantHealthLabel === "Critical" || tenantHealthLabel === "Degraded"
      ? doctorLabel === "Fail" || doctorLabel === "Warn"
        ? "Open Runtime Doctor report and resolve failing checks"
        : "Investigate platform health evidence"
      : null);

  const lastVerificationPreview =
    input.lastVerification ?? primary?.occurredAt ?? null;

  const runtimeHealthDomains = deriveRuntimeHealthDomains({
    customer,
    tenantHealth,
    integrations,
    knowledge,
    dns,
    ssl,
    platformVerifications: input.platformVerifications,
    attentionProblem,
    doctorMessage,
    recommendedAction: platformRecommendedPreview,
    lastVerification: lastVerificationPreview,
  });
  const runtimeRows = flattenDomainComponents(runtimeHealthDomains);
  const conciergeRow = runtimeRows.find((r) => r.id === "concierge_runtime");

  const platformHealthBase = derivePlatformHealthVerdict({
    health: tenantHealthLabel,
    doctor: doctorLabel,
    conciergeRuntimeLabel: conciergeRow?.label,
    attentionProblem,
    doctorMessage,
  });

  const platformRecommended =
    recommendedFromIncident ||
    (platformHealthBase.label === "Critical" || platformHealthBase.label === "Degraded"
      ? doctorLabel === "Fail" || doctorLabel === "Warn"
        ? "Open Runtime Doctor report and resolve failing checks"
        : "Investigate platform health evidence"
      : null);

  const doctorRecommended =
    recommendedFromIncident ||
    (doctorLabel === "Fail"
      ? "Resolve failing Runtime Doctor checks"
      : doctorLabel === "Warn"
        ? "Review Runtime Doctor warnings"
        : doctorLabel === "Not scanned"
          ? "Run Runtime Doctor"
          : null);

  const rootCause =
    platformHealthBase.reason ||
    primary?.problem ||
    doctorMessage ||
    null;

  const evidence: EvidenceRef[] = [];
  if (platformHealthBase.evidence) {
    evidence.push({
      kind: "health",
      label: "Platform health",
      value: platformHealthBase.evidence,
      url: null,
      observedAt: input.lastVerification ?? null,
    });
  }
  if (doctorMessage) {
    evidence.push({
      kind: "doctor_check",
      label: "Runtime Doctor",
      value: doctorMessage,
      url: null,
      observedAt: input.lastVerification ?? null,
    });
  }
  for (const inc of incidents) {
    evidence.push(...inc.evidence);
  }
  const dnsSlice = buildDns(dns);
  if (dnsSlice.evidence) {
    evidence.push({
      kind: "dns",
      label: "DNS",
      value: dnsSlice.evidence,
      url: null,
      observedAt: null,
    });
  }
  const sslSlice = buildSsl(ssl);
  if (sslSlice.evidence) {
    evidence.push({
      kind: "ssl",
      label: "SSL",
      value: sslSlice.evidence,
      url: null,
      observedAt: null,
    });
  }
  const knowledgeSlice = buildKnowledge(knowledge);
  if (knowledge && !knowledge.ready) {
    evidence.push({
      kind: "knowledge",
      label: "Knowledge Base",
      value: knowledge.detail,
      url: null,
      observedAt: null,
    });
  }

  // Container logs attach via Evidence Center (View Logs → GET .../logs).
  // Keep a stub entry so timelines always include the logs source slot.
  const logAttachment: LogAttachmentStub = {
    status: "structured_for_later",
    message:
      "Container logs load through Verification Center (View Logs). Full log search (ELK) is not implemented.",
    entries: [
      {
        kind: "log_placeholder",
        label: "Container logs",
        value: "Open Verification Center → View Logs to fetch tenant stack output",
        url: null,
        observedAt: null,
      },
    ],
  };

  const deployment = deriveVersionFields(customer, tenantHealth, platformVersion);

  const lastVerification = lastVerificationPreview;
  const verificationTimeline = buildVerificationTimeline([
    ...normalizeVerificationResults(input.platformVerifications),
    ...normalizeVerificationResults(tenantHealth?.verifications),
    ...dnsSlice.verifications,
    ...sslSlice.verifications,
    ...normalizeVerificationResults(
      (integrations ?? []).flatMap((row) => row.verifications ?? []),
    ),
  ]);

  return {
    slug,
    displayName: input.displayName ?? null,
    framing: OPERATIONAL_FRAMING,
    platformHealth: {
      ...platformHealthBase,
      recommendedAction: platformRecommended,
    },
    tenantHealth: tenantHealthLabel,
    incident: primary,
    incidents,
    runtimeDoctor: {
      label: doctorLabel,
      raw: doctorRaw,
      currentResult: doctorLabel,
      rootCause:
        doctorLabel === "Pass"
          ? null
          : rootCause ||
            (doctorLabel === "Not scanned"
              ? "No verified doctor scan for this tenant"
              : null),
      recommendedAction: doctorRecommended,
      reportHref: `#runtime-doctor-${slug}`,
      reportLabel: "View Runtime Doctor Report",
      lastScanAt: lastVerification,
    },
    productionReadiness,
    deployment,
    runtimeHealthDomains,
    runtimeHealth: runtimeRows,
    dns: dnsSlice,
    ssl: sslSlice,
    knowledge: knowledgeSlice,
    rootCause,
    recommendedAction: platformRecommended || doctorRecommended,
    evidence,
    confidence: buildConfidence(input.confidenceScore, input.confidenceReasons),
    lastVerification,
    verificationTimeline,
    logAttachment,
    onboarding,
  };
}
